import type { SchaefflerFormDTO } from "~/features/cms/types/dto";
import { renderCMSComponent } from "~/features/cms/componentFactory";
import { Button, Stack } from "~/common/mui";
import { FormProvider, useForm } from "react-hook-form";
import { useState } from "react";
import { trackSubmit } from "~/features/cms/schaefflerForm/analytics";
import { Snackbar } from "~/common/mui/Snackbar/Snackbar";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/mui/Alert/Alert";
import { makeExternallyStyledBaseElement } from "~/features/cms/utils/styles";
import { submitSchaefflerForm } from "./submitSchaefflerForm";

export interface SchaefflerFormProps extends Omit<SchaefflerFormDTO, "type"> {
  className?: string;
  onSuccess?: () => void;
}

export const SchaefflerForm = (props: SchaefflerFormProps) => {
  const { t } = useTranslation();
  const children = [...props.hiddenFields, ...props.rows];

  const formProps = useForm();
  const [loading, setLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);

  return (
    <FormProvider {...formProps}>
      <Form
        rootStyles={props.rootStyles}
        noValidate
        onSubmit={formProps.handleSubmit(
          async (values) => {
            setLoading(true);
            try {
              await submitSchaefflerForm(values);
              trackSubmit(true);
              setShowSnackbar(true);
              props.onSuccess?.();
              formProps.reset();
            } catch {
              trackSubmit(false);
            } finally {
              setLoading(false);
            }
          },
          () => trackSubmit(false),
        )}
      >
        <Stack gap={4}>
          {children.map((item, index) => renderCMSComponent(item, undefined, index))}
          <Button type="submit" loading={loading}>
            {t("contactUsPage.form.ok")}
          </Button>
        </Stack>
      </Form>
      <Snackbar open={showSnackbar} onClose={() => setShowSnackbar(false)}>
        <Alert severity="success" onClose={() => setShowSnackbar(false)} sx={{ boxShadow: 2 }}>
          {t("feature.cmsContactForm.success")}
        </Alert>
      </Snackbar>
    </FormProvider>
  );
};

const Form = makeExternallyStyledBaseElement("form");
